import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { fileService } from '../../services/fileService';
import { tryParseMediaProcessingResult } from '../../types/multimodal';
import { MediaProcessingStatus } from './MediaProcessingStatus';

export interface ImageMessageProps {
  fileId: string;
  processedContentJson?: string | null;
}

/**
 * Renders an image attachment with OCR/caption (if available).
 */
export const ImageMessage = ({ fileId, processedContentJson }: ImageMessageProps) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const result = useMemo(() => tryParseMediaProcessingResult(processedContentJson), [processedContentJson]);

  useEffect(() => {
    let active = true;
    let urlToRevoke: string | null = null;

    (async () => {
      try {
        const resp = await fileService.downloadFileBlob(fileId);
        const blob = resp.data;
        urlToRevoke = URL.createObjectURL(blob);
        if (active) setObjectUrl(urlToRevoke);
      } catch {
        if (active) setObjectUrl(null);
      }
    })();

    return () => {
      active = false;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [fileId]);

  const download = async () => {
    const resp = await fileService.downloadFileBlob(fileId);
    const blob = resp.data;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attachment-${fileId}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Image</Typography>
        <MediaProcessingStatus processedContentJson={processedContentJson} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} variant="outlined">
          Download
        </Button>
      </Box>

      {objectUrl && (
        <Box
          component="img"
          src={objectUrl}
          alt="attachment"
          sx={{ maxWidth: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
        />
      )}

      {result?.caption && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {result.caption}
        </Typography>
      )}

      {result?.description && (
        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {result.description}
        </Typography>
      )}

      {result?.extractedText && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
          {result.extractedText}
        </Typography>
      )}
    </Box>
  );
};

